import { TrendingUp, TrendingDown, Minus, Banknote, Package, Percent, PieChart } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatVNDShort, formatPercent } from '@/lib/format';
import type { ReportTotals } from '@/lib/api';

export interface ReportKpisProps {
  totals: ReportTotals;
  periodLabel: string;
  compare?: {
    totals: ReportTotals;
    periodLabel: string;
  } | null;
}

interface BaseKpiItem {
  label: string;
  icon: LucideIcon;
  iconClass: string;
}

function DeltaIndicator({ pct, suffix }: { pct: number; suffix?: string }) {
  if (Math.abs(pct) < 0.05) {
    return (
      <span className="inline-flex items-center gap-0.5 text-muted-foreground">
        <Minus className="h-3 w-3" />
        ±0%
      </span>
    );
  }
  const Icon = pct > 0 ? TrendingUp : TrendingDown;
  const colorClass = pct > 0 ? 'text-cat-graphic' : 'text-destructive';
  return (
    <span className={cn('inline-flex items-center gap-0.5 font-medium', colorClass)}>
      <Icon className="h-3 w-3" strokeWidth={2.5} />
      {pct > 0 ? '+' : ''}
      {pct.toFixed(1)}%{suffix}
    </span>
  );
}

function delta(current: number, prev: number): number {
  if (prev <= 0) return 0;
  return ((current - prev) / prev) * 100;
}

export function ReportKpis({ totals, periodLabel, compare }: ReportKpisProps) {
  const baseItems: Array<
    BaseKpiItem & {
      currentValue: string;
      currentNum: number;
      prevValue: string;
      prevNum: number;
      intraDelta?: number;
    }
  > = [
    {
      label: 'Doanh thu',
      icon: Banknote,
      iconClass: 'text-primary',
      currentValue: formatVNDShort(totals.revenue_vnd),
      currentNum: totals.revenue_vnd,
      prevValue: compare ? formatVNDShort(compare.totals.revenue_vnd) : '',
      prevNum: compare?.totals.revenue_vnd ?? 0,
      intraDelta: totals.delta_revenue_pct,
    },
    {
      label: 'Sản lượng',
      icon: Package,
      iconClass: 'text-cat-oled',
      currentValue: totals.units_sold.toLocaleString('vi-VN'),
      currentNum: totals.units_sold,
      prevValue: compare ? compare.totals.units_sold.toLocaleString('vi-VN') : '',
      prevNum: compare?.totals.units_sold ?? 0,
      intraDelta: totals.delta_units_pct,
    },
    {
      label: 'Gross Margin',
      icon: Percent,
      iconClass: 'text-cat-graphic',
      currentValue: formatPercent(totals.margin_pct, 1),
      currentNum: totals.margin_pct,
      prevValue: compare ? formatPercent(compare.totals.margin_pct, 1) : '',
      prevNum: compare?.totals.margin_pct ?? 0,
    },
    {
      label: 'Thị phần',
      icon: PieChart,
      iconClass: 'text-cat-focal',
      currentValue: formatPercent(totals.market_share_revenue_pct, 1),
      currentNum: totals.market_share_revenue_pct,
      prevValue: compare ? formatPercent(compare.totals.market_share_revenue_pct, 1) : '',
      prevNum: compare?.totals.market_share_revenue_pct ?? 0,
    },
  ];

  return (
    <section className="grid grid-cols-2 gap-3 md:grid-cols-4 print-no-break">
      {baseItems.map((item) => (
        <div
          key={item.label}
          className={cn(
            'rounded-lg border bg-card p-4 shadow-sm',
            'print:rounded-md print:border-border print:shadow-none'
          )}
        >
          <div className="flex items-start justify-between">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {item.label}
            </div>
            <item.icon className={cn('h-4 w-4', item.iconClass)} />
          </div>

          {/* Current value */}
          <div className="mt-2 text-2xl font-bold tabular-nums leading-none">
            {item.currentValue}
          </div>
          <div className="mt-0.5 text-[10px] text-muted-foreground">{periodLabel}</div>

          {/* Compare block (when compare on) */}
          {compare ? (
            <div className="mt-3 border-t pt-2">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-xs tabular-nums text-muted-foreground">
                  {item.prevValue}
                </span>
                <DeltaIndicator pct={delta(item.currentNum, item.prevNum)} />
              </div>
              <div className="mt-0.5 text-[10px] text-muted-foreground">
                {compare.periodLabel}
              </div>
            </div>
          ) : (
            <div className="mt-2 flex items-center gap-1 text-[11px] text-muted-foreground">
              {item.intraDelta !== undefined ? (
                <>
                  <DeltaIndicator pct={item.intraDelta} />
                  <span>nội kỳ</span>
                </>
              ) : null}
            </div>
          )}
        </div>
      ))}
    </section>
  );
}
